import React, { useState, ReactElement } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { type Swiper as SwiperType } from "swiper";
import "swiper/swiper-bundle.min.css";
import { useMediaQuery } from "@shared/hooks/useMediaQuery";
import breakpoints from "@shared/theme/props/breakpoints";
import { enums, enumsTypes } from "@shared/constants";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import ParsedHtml from "@ui-kit/typography/ParsedHtml/ParsedHtml.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import { AttractionMembershipItem } from "./AttractionMembershipItem.web";
import { AttractionProductItem } from "./AttractionProductItem.web";

const { lg } = breakpoints;

type AttractionItemType = {
  type: enumsTypes.AttractionChildTypeMapIdType;
  title: string;
  description: string;
  ids: number[];
  sort_order: number;
};

export interface AttractionSliderWithHeaderProps {
  attractionItem: AttractionItemType;
  venue: NestedPublicVenueSerializer;
  itemData: ProductTypeSerializer[] | PublicMembershipGroupSerializer[];
  isLoading: boolean;
  isBasketUpdating: boolean;
  renderAsGrid?: boolean;
}

/**
 * Swiper container styles for attraction slider
 */
const swiperContainerStyles = (itemType: number) => ({
  width: "100%",
  "& .swiper-container": {
    marginLeft: "0 !important",
    marginRight: "0 !important",
    width: "100%",
  },
  "& .swiper-slide": {
    maxWidth:
      itemType === enums.ATTRACTION_CHILD_TYPE_MAP.PRODUCT.id
        ? "227px"
        : "380px",
    height: "auto",
  },
  [`@media only screen and (max-width: ${lg}px)`]: {
    "& .swiper-slide": {
      maxWidth:
        itemType === enums.ATTRACTION_CHILD_TYPE_MAP.PRODUCT.id
          ? "190px"
          : "240px",
      height: "auto",
    },
  },
});

export const AttractionSliderWithHeader: React.FC<
  AttractionSliderWithHeaderProps
> = ({
  attractionItem,
  venue,
  itemData,
  isLoading,
  isBasketUpdating,
  renderAsGrid = false,
}) => {
  const { t } = useTranslation("purchases");
  const [isTablet] = useMediaQuery(`(min-width: 0px) and (max-width: ${lg}px)`);

  // handle swiping with the arrow buttons
  const [swiperObject, setSwiperObject] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // display 2 slides on smaller screens or if item type is a membership
  const numDisplaySlides =
    isTablet ||
    attractionItem.type === enums.ATTRACTION_CHILD_TYPE_MAP.MEMBERSHIP.id
      ? 2
      : 3;

  const skeletonItemArray =
    attractionItem.type === enums.ATTRACTION_CHILD_TYPE_MAP.MEMBERSHIP.id
      ? [1, 2]
      : [1, 2, 3];

  const getSlideDisplay = (
    item?:
      | ProductTypeSerializer
      | PublicMembershipGroupSerializer
      | { id: number }
  ): ReactElement => {
    // display the loaded items based on item type (product or membership)
    switch (attractionItem.type) {
      case enums.ATTRACTION_CHILD_TYPE_MAP.PRODUCT.id:
        return (
          <AttractionProductItem
            key={`product-${item?.id}`}
            venue={venue}
            product={item as ProductTypeSerializer}
            isLoading={isLoading}
            isBasketUpdating={isBasketUpdating}
          />
        );
      case enums.ATTRACTION_CHILD_TYPE_MAP.MEMBERSHIP.id:
        return (
          <AttractionMembershipItem
            key={`membership-${item?.id}`}
            venue={venue}
            membershipGroup={item as PublicMembershipGroupSerializer}
            isLoading={isLoading}
          />
        );
      default:
        return <></>;
    }
  };

  const getRenderedElements = (): ReactElement[] => {
    if (isLoading || !itemData) {
      return skeletonItemArray.map((index) => getSlideDisplay({ id: index }));
    }
    if (itemData.length === 0) {
      return [
        <Text
          fontWeight="bold"
          fontSize={{ base: "lg", lg: "3xl" }}
          key="no-items-available"
        >
          {t("No items available.")}
        </Text>,
      ];
    }
    return itemData.map(getSlideDisplay);
  };

  return (
    <>
      {/* Header section with arrows */}
      <Flex pb="md" alignItems="center" justifyContent="space-between">
        <Text
          fontSize={{ base: "md", sm: "lg" }}
          fontWeight="bold"
          color="primary.600"
        >
          {attractionItem.title}
        </Text>
        <Flex
          hidden={
            isLoading || itemData?.length <= numDisplaySlides || renderAsGrid
          }
        >
          <IconButton
            iconName="ChevronLeftRounded"
            iconColor={isBeginning ? "gray.400" : "font.dark"}
            iconSize={{ base: "30px", lg: "32px" }}
            iconOutline={false}
            colorPalette="light"
            disabled={isBeginning}
            aria-label={t("Previous item set")}
            onClick={() => {
              if (swiperObject) {
                swiperObject.slideTo(swiperObject.activeIndex - 1);
                setIsBeginning(swiperObject.isBeginning);
                setIsEnd(swiperObject.isEnd);
              }
            }}
          />

          <Box minWidth="element.xs" />
          <IconButton
            iconName="ChevronRightRounded"
            iconColor={isEnd ? "gray.400" : "font.dark"}
            iconSize={{ base: "30px", lg: "32px" }}
            iconOutline={false}
            colorPalette="light"
            disabled={isEnd}
            aria-label={t("Next item set")}
            onClick={() => {
              if (swiperObject) {
                swiperObject.slideTo(swiperObject.activeIndex + 1);
                setIsBeginning(swiperObject.isBeginning);
                setIsEnd(swiperObject.isEnd);
              }
            }}
          />
        </Flex>
      </Flex>
      {/* Section description */}
      <Text pb="lg" fontSize="md" color="fg.muted">
        {ParsedHtml({ htmlString: attractionItem.description })}
      </Text>
      {/* Item slider section */}
      <Flex width="100%" maxWidth="100%" overflow="hidden">
        {/* TODO: Move swiper styles to CSS module or create SwiperContainer component */}
        {/* eslint-disable-next-line showpass-custom/prefer-semantic-props */}
        <Flex width="100%" css={swiperContainerStyles(attractionItem.type)}>
          {renderAsGrid ? (
            <Flex gap="lg" flexWrap="wrap" w="100%" position="relative">
              {getRenderedElements().map((element) => (
                <Box
                  key={element.key}
                  w={{
                    base: "calc((100% - 20px) / 2)",
                    xs: "calc((100% - 40px) / 3)",
                    sm: "calc((100% - 60px) / 4)",
                  }}
                >
                  {element}
                </Box>
              ))}
            </Flex>
          ) : (
            <Swiper
              spaceBetween={20}
              slidesPerView={numDisplaySlides}
              onSwiper={(swiper) => setSwiperObject(swiper)}
              onActiveIndexChange={(swiper) => {
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
              }}
            >
              {getRenderedElements().map((element) => (
                <SwiperSlide key={element.key}>{element}</SwiperSlide>
              ))}
            </Swiper>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default AttractionSliderWithHeader;
